import { useCallback, useEffect, useState } from 'react';
import { cotizacionDetalleService } from './service';
import { SitioEntrega, SitioEntregaSugerida } from './types';
import { ubicacionService } from '../../../clientes/services/ubicacionService';
import { Departamento, Ciudad } from '../../../clientes/types/Cliente';

// SPEC-159 — una entrega está "especificada" si tiene departamento/ciudad persistidos
// (RN-002/4.2). Determina si el bloque prellena desde lo guardado o desde la sugerencia
// del cliente. v1.1 (RN-011): sin dirección.
function esEspecificada(entrega: SitioEntrega): boolean {
  return Boolean(entrega.departamentoId || entrega.ciudadId);
}

interface UseSitioEntregaParams {
  bolsaId: string;
  entrega: SitioEntrega;
  entregaSugerida: SitioEntregaSugerida;
  soloLectura: boolean;
}

export interface UseSitioEntregaResult {
  departamentoId: number | null;
  ciudadId: number | null;
  departamentoNombre: string | null;
  ciudadNombre: string | null;
  departamentos: Departamento[];
  ciudades: Ciudad[];
  guardando: boolean;
  error: string | null;
  cambiarDepartamento: (departamentoId: number | null) => void;
  cambiarCiudad: (ciudadId: number | null) => void;
}

// SPEC-159 (TASK-012) — estado del sitio de entrega en el Detalle: prefill (RN-002/003),
// cascada departamento→ciudad (RN-006) y auto-guardado (patrón freight). Reutiliza el
// catálogo de geografía existente (ubicacionService). v1.1 (RN-011): sin dirección.
export function useSitioEntrega({
  bolsaId,
  entrega,
  entregaSugerida,
  soloLectura,
}: UseSitioEntregaParams): UseSitioEntregaResult {
  const [departamentoId, setDepartamentoId] = useState<number | null>(null);
  const [ciudadId, setCiudadId] = useState<number | null>(null);
  const [departamentoNombre, setDepartamentoNombre] = useState<string | null>(null);
  const [ciudadNombre, setCiudadNombre] = useState<string | null>(null);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [ciudades, setCiudades] = useState<Ciudad[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill una sola vez por bolsa (RN-003: no re-sugerir sobre lo ya especificado).
  useEffect(() => {
    const fuente: SitioEntrega =
      esEspecificada(entrega) || !entregaSugerida ? entrega : entregaSugerida;
    setDepartamentoId(fuente.departamentoId);
    setCiudadId(fuente.ciudadId);
    setDepartamentoNombre(fuente.departamento);
    setCiudadNombre(fuente.ciudad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolsaId]);

  // Catálogo de departamentos (una vez).
  useEffect(() => {
    let vivo = true;
    ubicacionService
      .listarDepartamentos()
      .then((d) => vivo && setDepartamentos(d))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);

  // Ciudades del departamento seleccionado (para poblar el select en cascada).
  useEffect(() => {
    if (departamentoId == null) {
      setCiudades([]);
      return;
    }
    let vivo = true;
    ubicacionService
      .listarCiudades(departamentoId)
      .then((c) => vivo && setCiudades(c))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [departamentoId]);

  const persistir = useCallback(
    async (payload: { departamentoId: number | null; ciudadId: number | null }) => {
      if (soloLectura) return;
      setGuardando(true);
      setError(null);
      try {
        const guardada = await cotizacionDetalleService.actualizarSitioEntrega(bolsaId, payload);
        setDepartamentoId(guardada.departamentoId);
        setCiudadId(guardada.ciudadId);
        setDepartamentoNombre(guardada.departamento);
        setCiudadNombre(guardada.ciudad);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Error al guardar el sitio de entrega');
      } finally {
        setGuardando(false);
      }
    },
    [bolsaId, soloLectura],
  );

  // RN-006 — cambiar departamento reinicia la ciudad y auto-guarda (ciudad en null).
  const cambiarDepartamento = useCallback(
    (nuevoDepartamentoId: number | null) => {
      setDepartamentoId(nuevoDepartamentoId);
      setCiudadId(null);
      setCiudadNombre(null);
      const depto = departamentos.find((d) => d.id === nuevoDepartamentoId) ?? null;
      setDepartamentoNombre(depto?.nombre ?? null);
      void persistir({ departamentoId: nuevoDepartamentoId, ciudadId: null });
    },
    [departamentos, persistir],
  );

  const cambiarCiudad = useCallback(
    (nuevaCiudadId: number | null) => {
      setCiudadId(nuevaCiudadId);
      const ciu = ciudades.find((c) => c.id === nuevaCiudadId) ?? null;
      setCiudadNombre(ciu?.nombre ?? null);
      void persistir({ departamentoId, ciudadId: nuevaCiudadId });
    },
    [ciudades, departamentoId, persistir],
  );

  return {
    departamentoId,
    ciudadId,
    departamentoNombre,
    ciudadNombre,
    departamentos,
    ciudades,
    guardando,
    error,
    cambiarDepartamento,
    cambiarCiudad,
  };
}
